import { MouseEvent as ReactMouseEvent, useCallback } from 'react';
import type { GridRowParams } from '@mui/x-data-grid-pro';
import type { GalleryState } from 'components/common/gallery';
import { getRowFromEvent } from 'components/common/dataGrid/dom';
import { useDocumentationStore } from 'store/documentation';
import {
  DocumentationViewItem,
  isDirectoryItemType,
} from 'store/documentation/model/types';
import { findGallerySlideOfRow, getRowId } from '../model';

interface GridRowInteractionsParams {
  rows: DocumentationViewItem[];
  setGallery: (state: GalleryState) => void;
  navigateToDirectory: (directoryId: string, directoryName?: string) => Promise<void>;
  openContextMenu: (event: MouseEvent) => void;
}

/** Only the clicked row is read, so any `GridRowParams` satisfies it. */
type RowDoubleClickParams = Pick<GridRowParams<DocumentationViewItem>, 'row'>;

/** Row double click (open folder / preview) and right click (context menu). */
export function useGridRowInteractions({
  rows,
  setGallery,
  navigateToDirectory,
  openContextMenu,
}: GridRowInteractionsParams): {
  handleRowDoubleClick: (params: RowDoubleClickParams) => void;
  handleRowContextMenu: (event: ReactMouseEvent<HTMLDivElement>) => void;
} {
  const setSelectedItems = useDocumentationStore.use.setSelectedItems();
  const clearUploadedDirectoryHighlight =
    useDocumentationStore.use.clearUploadedDirectoryHighlight();
  const clearUploadedDocumentationHighlight =
    useDocumentationStore.use.clearUploadedDocumentationHighlight();

  // Points the gallery at the file (the context menu edits it) and optionally
  // opens the preview. The slide is derived from the grid rows, so it does not
  // wait for the store's gallery files to catch up.
  const showFileInGallery = useCallback(
    (file: DocumentationViewItem, open: boolean) => {
      const slide = findGallerySlideOfRow(rows, file);
      if (slide < 0) {
        return;
      }
      if (open) {
        clearUploadedDocumentationHighlight(file.localId);
      }
      setGallery({ open, slide });
    },
    [rows, setGallery, clearUploadedDocumentationHighlight]
  );

  const handleRowDoubleClick = useCallback(
    ({ row }: RowDoubleClickParams) => {
      if (isDirectoryItemType(row.type)) {
        if (!row._id) return;
        clearUploadedDirectoryHighlight(row._id);
        void navigateToDirectory(row._id, row.name);
        return;
      }
      showFileInGallery(row, true);
    },
    [navigateToDirectory, showFileInGallery, clearUploadedDirectoryHighlight]
  );

  const handleRowContextMenu = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();

      const row = getRowFromEvent(event, rows, getRowId);
      if (!row) return;

      // Context menu actions are single-item only, so right-click always
      // sets one selected row.
      setSelectedItems(new Set([row]));
      showFileInGallery(row, false);
      openContextMenu(event.nativeEvent);
    },
    [rows, setSelectedItems, showFileInGallery, openContextMenu]
  );

  return { handleRowDoubleClick, handleRowContextMenu };
}
